import { useState, useEffect, useMemo, useCallback } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { periodsForRange, fmtPeriod } from './capabilityAnalyticsUtils';
import { NarrativeCard } from './NarrativeCard';
import { TrendCard } from './TrendCard';
import { HeatmapCard } from './HeatmapCard';
import { ScheduleDialog } from './ScheduleDialog';

export function CapabilityAnalytics({ selectedOrg, canAdmin, onDrill }) {
  const [loading, setLoading] = useState(true);
  const [trend, setTrend] = useState(null);
  const [heatmap, setHeatmap] = useState(null);
  const [scope, setScope] = useState('org');
  const [metric, setMetric] = useState('role_coverage_pct');
  const [range, setRange] = useState('last_12_months');
  const [custom, setCustom] = useState({ from: '', to: '' });
  const [capturing, setCapturing] = useState(false);
  const [yrLoading, setYrLoading] = useState(false);
  const [narrative, setNarrative] = useState(null);
  const [narrLoading, setNarrLoading] = useState(false);
  const [narrCached, setNarrCached] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailNote, setEmailNote] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [schedule, setSchedule] = useState(null);
  const [schedOpen, setSchedOpen] = useState(false);
  const [schedSaving, setSchedSaving] = useState(false);
  const [schedSending, setSchedSending] = useState(false);
  const [schedForm, setSchedForm] = useState({ enabled: false, recipients: '', day_of_month: 1, note: '', attach_pdf: false, attach_mode: 'always' });
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [heatmapCohort, setHeatmapCohort] = useState('all');

  const visiblePeriods = useMemo(
    () => periodsForRange(trend?.periods, range, custom),
    [trend, range, custom],
  );

  const loadHistory = useCallback(async () => {
    if (!selectedOrg) return;
    try {
      const { data } = await api.get(`/capability-inventory/update-schedule/history?organization_id=${selectedOrg}`);
      setHistory(data.dispatches || []);
    } catch { /* non-blocking */ }
  }, [selectedOrg]);

  const loadSchedule = useCallback(async () => {
    if (!selectedOrg) return;
    try {
      const { data } = await api.get(`/capability-inventory/update-schedule?organization_id=${selectedOrg}`);
      setSchedule(data);
    } catch { /* non-blocking */ }
  }, [selectedOrg]);
  useEffect(() => { loadSchedule(); }, [loadSchedule]);

  const openSchedule = () => {
    setSchedForm({
      enabled: !!schedule?.enabled,
      recipients: (schedule?.recipients || []).join(', '),
      day_of_month: schedule?.day_of_month || 1,
      note: schedule?.note || '',
      attach_pdf: !!schedule?.attach_pdf,
      attach_mode: schedule?.attach_mode || 'always',
    });
    setHistoryOpen(false);
    loadHistory();
    setSchedOpen(true);
  };
  const saveSchedule = async () => {
    setSchedSaving(true);
    try {
      const recipients = schedForm.recipients.split(/[,\n;]+/).map(s => s.trim()).filter(Boolean);
      const { data } = await api.put('/capability-inventory/update-schedule', {
        organization_id: selectedOrg,
        enabled: schedForm.enabled,
        recipients,
        day_of_month: Number(schedForm.day_of_month) || 1,
        note: schedForm.note.trim() || undefined,
        attach_pdf: schedForm.attach_pdf,
        attach_mode: schedForm.attach_mode,
      });
      setSchedule(data);
      toast.success(schedForm.enabled ? 'Monthly update scheduled' : 'Schedule saved');
      setSchedOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save the schedule');
    } finally {
      setSchedSaving(false);
    }
  };
  const sendScheduleNow = async () => {
    setSchedSending(true);
    try {
      const recipients = schedForm.recipients.split(/[,\n;]+/).map(s => s.trim()).filter(Boolean);
      // Persist current recipients first so send-now uses them.
      await api.put('/capability-inventory/update-schedule', {
        organization_id: selectedOrg, enabled: schedForm.enabled, recipients,
        day_of_month: Number(schedForm.day_of_month) || 1, note: schedForm.note.trim() || undefined,
        attach_pdf: schedForm.attach_pdf, attach_mode: schedForm.attach_mode,
      });
      const { data } = await api.post('/capability-inventory/update-schedule/send-now', { organization_id: selectedOrg });
      toast.success(`Update sent to ${data.sent} recipient${data.sent !== 1 ? 's' : ''}`);
      loadSchedule();
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send the update');
    } finally {
      setSchedSending(false);
    }
  };

  const downloadUpdate = async () => {
    setExporting(true);
    try {
      const { data } = await api.post('/capability-inventory/trend-narrative/export', { organization_id: selectedOrg });
      const md = `# ${data.title}\n\n${data.markdown}\n`;
      const blob = new Blob([md], { type: 'text/markdown;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${data.title.replace(/[^a-z0-9]+/gi, '_')}.md`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Capability update downloaded');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not export the update');
    } finally {
      setExporting(false);
    }
  };

  const sendUpdate = async () => {
    if (!emailTo || !emailTo.includes('@')) { toast.error('Enter a valid email address'); return; }
    setEmailSending(true);
    try {
      await api.post('/capability-inventory/trend-narrative/email', {
        organization_id: selectedOrg,
        recipient_email: emailTo.trim(),
        note: emailNote.trim() || undefined,
      });
      toast.success(`Capability update emailed to ${emailTo.trim()}`);
      setShowEmail(false); setEmailTo(''); setEmailNote('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send the email');
    } finally {
      setEmailSending(false);
    }
  };

  const loadNarrative = useCallback(async (regenerate = false) => {
    if (!selectedOrg) return;
    setNarrLoading(true);
    try {
      const { data } = await api.post('/capability-inventory/trend-narrative',
        { organization_id: selectedOrg, regenerate });
      setNarrative(data.narrative);
      setNarrCached(!!data.cached);
    } catch (e) {
      setNarrative(null);
      if (regenerate) toast.error(e?.response?.data?.detail || 'Could not generate the narrative');
    } finally {
      setNarrLoading(false);
    }
  }, [selectedOrg]);

  const loadAll = useCallback(async () => {
    if (!selectedOrg) { setLoading(false); return; }
    setLoading(true);
    try {
      const [t, h] = await Promise.all([
        api.get(`/capability-inventory/trend?organization_id=${selectedOrg}`),
        api.get(`/capability-inventory/heatmap?organization_id=${selectedOrg}`),
      ]);
      setTrend(t.data);
      setHeatmap(h.data);
    } catch {
      toast.error('Could not load capability analytics');
    } finally {
      setLoading(false);
    }
  }, [selectedOrg]);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { setNarrative(null); loadNarrative(false); }, [loadNarrative]);

  const captureSnapshot = async () => {
    setCapturing(true);
    try {
      const { data } = await api.post('/capability-inventory/snapshot', { organization_id: selectedOrg });
      toast.success(`Snapshot captured for ${fmtPeriod(data.period)}`);
      await loadAll();
      loadNarrative(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not capture snapshot');
    } finally {
      setCapturing(false);
    }
  };

  const downloadYearReview = async () => {
    setYrLoading(true);
    try {
      const res = await api.post('/capability-inventory/year-review',
        { organization_id: selectedOrg, format: 'pdf' }, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Annual_Capability_Report.pdf';
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Annual capability report downloaded');
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not build the report');
    } finally {
      setYrLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-slate-400" data-testid="cap-analytics-loading">
        <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading analytics…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="cap-analytics">
      <NarrativeCard
        trend={trend}
        narrative={narrative}
        narrLoading={narrLoading}
        narrCached={narrCached}
        canAdmin={canAdmin}
        schedule={schedule}
        exporting={exporting}
        showEmail={showEmail}
        emailTo={emailTo}
        emailNote={emailNote}
        emailSending={emailSending}
        onRegenerate={loadNarrative}
        onDownload={downloadUpdate}
        onToggleEmail={() => setShowEmail(v => !v)}
        onEmailToChange={setEmailTo}
        onEmailNoteChange={setEmailNote}
        onSendEmail={sendUpdate}
        onOpenSchedule={openSchedule}
      />

      <TrendCard
        trend={trend}
        scope={scope}
        setScope={setScope}
        metric={metric}
        setMetric={setMetric}
        range={range}
        setRange={setRange}
        custom={custom}
        setCustom={setCustom}
        visiblePeriods={visiblePeriods}
        canAdmin={canAdmin}
        capturing={capturing}
        onCapture={captureSnapshot}
        yrLoading={yrLoading}
        onYearReview={downloadYearReview}
      />

      <HeatmapCard heatmap={heatmap} onDrill={onDrill} cohortFilter={heatmapCohort} onCohortFilterChange={setHeatmapCohort} />

      <ScheduleDialog
        open={schedOpen}
        onOpenChange={setSchedOpen}
        form={schedForm}
        setForm={setSchedForm}
        saving={schedSaving}
        sending={schedSending}
        onSave={saveSchedule}
        onSendNow={sendScheduleNow}
        history={history}
        historyOpen={historyOpen}
        setHistoryOpen={setHistoryOpen}
      />
    </div>
  );
}
